import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import { useIsAdmin } from '../lib/auth';
import { clearSwrCacheByPrefix, hasFreshSwrCache, swrLoad, writeSwrCache } from '../lib/swrCache';
import { confirmDialog, toast } from '../lib/dialogs';
import { useEventImageUrl } from '../lib/image-sas';
import type { ExtractedEvent, Venue, EventSeries } from '../types';
import EventDetailModal from '../components/EventDetailModal';
import EventDateMenu from '../components/EventDateMenu';
import EventTagChips from '../components/EventTagChips';
import EventEditModal from '../components/EventEditModal';
import { StaleRecurringChip } from '../components/StaleRecurringChip';
import { useEventRejection } from '../hooks/useEventRejection';
import CalendarView from '../components/CalendarView';
import EventFiltersBar from '../components/EventFiltersBar';
import OnViewShelf from '../components/OnViewShelf';import GroupBySelector from '../components/GroupBySelector';
import SectionTabs from '../components/SectionTabs';
import { EVENTS_TABS } from '../lib/section-tabs';
import { formatTag } from '../lib/tagTaxonomy';
import {
  type EventGroupNode,
  type GroupByKey,
  groupByLabel,
  groupEvents,
  groupingFromParam,
  groupingToParam,
} from '../lib/eventGrouping';
import {
  type FilterState,
  type Origin,
  applyFilters,
  DATE_WINDOW_ERROR,
  presetWindow,
  readFiltersFromUrl,
  writeFiltersToUrl,
} from '../lib/eventFilters';
import {
  DEFAULT_ORIGIN_ADDRESS,
  loadSavedOrigin,
  saveOriginToStorage,
} from '../lib/useStoredOrigin';
import { geocode } from '../lib/api';
import { dateTileParts, formatRange, isAllDayEvent, shortDateLabel, timePillParts } from '../lib/eventDates';
import { useFavorites, isEventHighlighted } from '../lib/favorites';
import type { DateCorrection } from '../lib/useRecalculateDate';
import { useTasteSettings } from '../lib/tasteSettings';
import {
  buildTasteProfile,
  scoreEvent,
  MIN_TASTE_FOR_SCORING,
  MIN_TASTE_SCORE_THRESHOLD
} from '../lib/tasteScore';
import { useTasteMatch } from '../lib/tasteMatch';
import MatchBadge from '../components/MatchBadge';
import EventReviewWarningChips from '../components/EventReviewWarningChips';
import {
  DateProofSchedule, DateProofWarning, dateProofRangeOptions, nonTemporalWarnings, useDateProofEvents,
} from '../lib/dateProofReview';
import EventReactionButtons from '../components/EventReactionButtons';
import EntityFollowChip from '../components/EntityFollowChip';
import { useIsAuthenticated } from '@azure/msal-react';
import { useScrollRestoration } from '../lib/useScrollRestoration';

type ViewMode = 'list' | 'calendar';
type Density = 'compact' | 'cards';

/** Sanitise a group bucket value for use as part of a DOM id / class. */
function cssSafe(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 64);
}

export default function HomePage() {
  const resolveImage = useEventImageUrl();
  const { pathname } = useLocation();
  const viewMode: ViewMode = pathname.endsWith('/calendar') ? 'calendar' : 'list';
  const [params, setParams] = useSearchParams();
  const [filters, setFiltersState] = useState<FilterState>(() => {
    const fromUrl = readFiltersFromUrl(params);
    // Origin URL params win. Otherwise fall back to localStorage so the user's
    // chosen address persists across sessions even when they navigate in via a
    // bare /events link.
    if (!fromUrl.origin) {
      const saved = loadSavedOrigin();
      if (saved) return { ...fromUrl, origin: saved.origin, maxDistanceMiles: saved.maxDistanceMiles };
    }
    return fromUrl;
  });
  const [density, setDensityState] = useState<Density>(() => (params.get('d') as Density) ?? 'compact');
  const [groupBy, setGroupByState] = useState<GroupByKey[]>(() => groupingFromParam(params.get('group')));
  const [favoritesOnly, setFavoritesOnly] = useState<boolean>(() => params.get('fav') === '1');
  const [tasteOnly, setTasteOnly] = useState<boolean>(() => params.get('taste') === '1');
  const favorites = useFavorites();
  const tasteSettings = useTasteSettings();
  const isAuthed = useIsAuthenticated();
  const [loadedEvents, setEvents] = useState<ExtractedEvent[] | null>(null);
  const events = useDateProofEvents(loadedEvents);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExtractedEvent | null>(null);

  // Remember the user's scroll position so leaving the list (e.g. into a venue
  // page or an event's source) and returning shortly after drops them back
  // where they were instead of at the top. Keyed by pathname so the list and
  // calendar views are tracked independently. Restore once events have loaded
  // so the target offset is reachable.
  useScrollRestoration(pathname, events !== null);
  const isAdmin = useIsAdmin();
  const [editingEvent, setEditingEvent] = useState<ExtractedEvent | null>(null);
  const { promptReject: openRejectModal, modal: rejectModal } = useEventRejection({
    onRejected: (event) => {
      setEvents((curr) => (curr ? curr.filter((e) => e.rowKey !== event.rowKey) : curr));
      setSelected((s) => (s && s.rowKey === event.rowKey ? null : s));
    },
  });
  const [adminVenues, setAdminVenues] = useState<Venue[]>([]);
  const [adminSeries, setAdminSeries] = useState<EventSeries[]>([]);

  // Server-supplied cosine similarity for the user's top tag-scored candidates
  // when "My taste only" is active. Empty by default; the tag-based filter
  // continues to work standalone if the request fails or embeddings are
  // missing.
  const [tasteEmbeddingScores, setTasteEmbeddingScores] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!isAuthed || !tasteOnly || !events || favorites.tasteIds.size < MIN_TASTE_FOR_SCORING) {
      setTasteEmbeddingScores(new Map());
      return;
    }
    const markedEvents = events.filter(e => favorites.tasteIds.has(e.rowKey));
    const profile = buildTasteProfile(markedEvents);
    if (profile.size < MIN_TASTE_FOR_SCORING) return;
    const candidates = events
      .filter(e => !profile.markedRowKeys.has(e.rowKey))
      .map(e => ({ id: e.rowKey, s: scoreEvent(e, profile, undefined, tasteSettings.weights).score }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 200)
      .map(x => x.id);
    if (candidates.length === 0) {
      setTasteEmbeddingScores(new Map());
      return;
    }
    let cancelled = false;
    api.post<{ scores: Record<string, number> }>(
      '/api/me/taste/embedding-scores',
      { candidateIds: candidates }
    )
      .then(res => {
        if (cancelled) return;
        const m = new Map<string, number>();
        for (const [k, v] of Object.entries(res.scores ?? {})) {
          if (typeof v === 'number') m.set(k, v);
        }
        setTasteEmbeddingScores(m);
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('embedding-scores fetch failed', err);
          setTasteEmbeddingScores(new Map());
        }
      });
    return () => { cancelled = true; };
  }, [isAuthed, tasteOnly, events, favorites.tasteIds, tasteSettings.weights]);

  // Global taste-match scorer — powers the match badge on every event row/card.
  // Registering the loaded events lets the badge (and other pages) score against
  // the user's marked-event profile. Blends in embedding scores when present.
  const tasteMatch = useTasteMatch();
  useEffect(() => {
    if (events && events.length > 0) tasteMatch.registerEvents(events);
  }, [events, tasteMatch]);
  const matchFor = useCallback(
    (e: ExtractedEvent) => tasteMatch.match(e, tasteEmbeddingScores.get(e.rowKey)),
    [tasteMatch, tasteEmbeddingScores]
  );

  // Admin: the venue + series lists exist only to populate the EventEditModal
  // pickers, so they are loaded when that modal first opens rather than on page
  // mount. Eagerly fetching them cost ~1.6 s of the cold-start critical path
  // (measured in the HAR that motivated docs/STARTUP_PERF_PLAN.md) on every
  // visit, for two dropdowns most visits never open.
  //
  // Venues reuse the shared `venues:public` SWR key, so opening the modal after
  // visiting /venues is instant, and existing venue mutations already invalidate
  // that key. Series uses the admin-only /api/mgmt/series (a different, larger
  // set than the public list), and is fetched directly rather than given a new
  // cache key that nothing would invalidate.
  const [adminPickersRequested, setAdminPickersRequested] = useState(false);
  useEffect(() => {
    if (isAdmin && editingEvent && !adminPickersRequested) setAdminPickersRequested(true);
  }, [isAdmin, editingEvent, adminPickersRequested]);

  useEffect(() => {
    if (!adminPickersRequested) return;
    let cancelled = false;
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (vs) => setAdminVenues(vs),
      isCancelled: () => cancelled,
    });
    api.get<EventSeries[]>('/api/mgmt/series')
      .then((ss) => { if (!cancelled) setAdminSeries(ss); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [adminPickersRequested]);

  const extraParams = (): Record<string, string | undefined> => ({
    d: density === 'compact' ? undefined : density,
    group: groupingToParam(groupBy),
    fav: favoritesOnly ? '1' : undefined,
    taste: tasteOnly ? '1' : undefined,
  });

  const setFilters = (updater: (f: FilterState) => FilterState) => {
    setFiltersState((prev) => {
      const next = updater(prev);
      writeFiltersToUrl(next, extraParams(), (p) => setParams(p, { replace: true }));
      saveOriginToStorage(
        next.origin
          ? { origin: next.origin, maxDistanceMiles: next.maxDistanceMiles }
          : null
      );
      return next;
    });
  };

  const setDensity = (d: Density) => {
    setDensityState(d);
    writeFiltersToUrl(
      filters,
      { ...extraParams(), d: d === 'compact' ? undefined : d },
      (p) => setParams(p, { replace: true })
    );
  };

  const setGroupBy = (next: GroupByKey[]) => {
    setGroupByState(next);
    writeFiltersToUrl(
      filters,
      { ...extraParams(), group: groupingToParam(next) },
      (p) => setParams(p, { replace: true })
    );
  };

  useEffect(() => {
    const window = presetWindow(filters);
    if (!window) {
      setError(DATE_WINDOW_ERROR);
      setEvents([]);
      return;
    }
    setError(null);
    const { from, to } = window;
    const fromIso = from.toISOString();
    const toIso = to.toISOString();
    // Include declined / inactive venue events on the wire only when the
    // viewer explicitly opted into 'Show all' — keeps the default payload
    // small and prevents leaking moderation noise to casual visitors.
    const includeHidden = filters.venueVisibility === 'all';
    const qs = `from=${fromIso}&to=${toIso}${includeHidden ? '&includeHidden=1' : ''}`;
    const key = `events:${fromIso}|${toIso}|h=${includeHidden ? 1 : 0}`;
    // Avoid a null → data flash: only show the loading skeleton when we
    // genuinely have nothing cached for this window. This payload is too large
    // for localStorage and lives in IndexedDB, so swrLoad delivers it a few ms
    // later rather than synchronously — hasFreshSwrCache answers "is it there?"
    // from a sync index so we still don't flash a spinner over it.
    if (!hasFreshSwrCache(key)) setEvents(null);
    let cancelled = false;
    void swrLoad<ExtractedEvent[]>({
      key,
      fetcher: () => api.get<ExtractedEvent[]>(`/api/events/published?${qs}`),
      onData: (data) => setEvents(data),
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, [filters.preset, filters.customFrom, filters.customTo, filters.venueVisibility]);

  // First-visit default: if the user has never set an origin (no URL param,
  // no localStorage entry), geocode the default address in the background and
  // seed the filter state. Subsequent visits re-use the saved origin without
  // hitting the API again. Empty deps = run once on mount.
  useEffect(() => {
    if (filters.origin) return;
    let cancelled = false;
    void (async () => {
      const r = await geocode(DEFAULT_ORIGIN_ADDRESS).catch(() => null);
      if (cancelled || !r) return;
      const origin: Origin = { label: DEFAULT_ORIGIN_ADDRESS, lat: r.lat, lng: r.lng };
      setFilters((f) => (f.origin ? f : { ...f, origin }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin: decline a venue so its events stop appearing on the public
  // list. PATCHes `reviewStatus: 'declined'` + `active: false` (matches
  // the Decline button on the Manage venues page) so the venue is
  // explicitly marked rejected rather than landing in the ambiguous
  // "legacy dismissed" bucket. Optimistically prunes events sharing the
  // venueId from local state and clears the SWR cache so other windows
  // refetch fresh data.
  async function hideVenue(venueId: string, venueName: string): Promise<void> {
    const label = venueName || 'this venue';
    if (!(await confirmDialog({
      title: 'Decline venue',
      body:
        `Decline "${label}"? All of its events will be hidden from the public list. ` +
        `You can re-approve it from the Manage venues page.`,
      confirmLabel: 'Decline',
      danger: true,
    }))) return;
    try {
      await api.patch(`/api/mgmt/venues/${encodeURIComponent(venueId)}`, {
        reviewStatus: 'declined',
        active: false,
        enrichmentStatus: 'skipped',
      });
      setEvents((curr) => curr ? curr.filter((e) => e.venueId !== venueId) : curr);
      clearSwrCacheByPrefix('events:');
      setSelected((s) => (s && s.venueId === venueId ? null : s));
    } catch (err) {
      // Surface failures to the user — the silent auth-pending error is
      // already handled by api.ts's redirect flow.
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not decline venue: ${msg}`);
    }
  }

  // Admin: hide a single event by marking it `status: 'rejected'`. The
  // public events endpoint only returns `approved` + `published`, so a
  // rejected event disappears from the public list immediately. When
  // `flagForAiTraining` is true, the backend also records
  // `flaggedForAiTraining` on the EventDecision row so we can review later
  // in Ops > AI training (helps tighten noise filters / extractor prompt).
  // Optimistically prunes the event from local state and clears the SWR
  // cache so other windows refetch fresh data.

  // Open the in-app reject confirmation modal. Closes any open detail panel
  // first, then delegates to the shared `useEventRejection` hook which
  // handles the API call + AI-training flag.
  function promptHideEvent(event: ExtractedEvent): void {
    setSelected((s) => (s && s.rowKey === event.rowKey ? null : s));
    openRejectModal(event);
  }

  // Admin: dismiss a series so all of its events stop appearing on the public
  // list. Mirrors `hideVenue` — sets both `status: 'dismissed'` and
  // `active: false` (either flag suppresses the events server-side) and
  // optimistically prunes events sharing the seriesId from local state.
  async function hideSeries(seriesId: string, seriesName: string): Promise<void> {
    const label = seriesName || 'this series';
    if (!(await confirmDialog({
      title: 'Dismiss series',
      body:
        `Dismiss "${label}"? All of its events will be hidden from the public list. ` +
        `You can re-activate it from the Manage series page.`,
      confirmLabel: 'Dismiss',
      danger: true,
    }))) return;
    try {
      await api.patch(`/api/mgmt/series/${encodeURIComponent(seriesId)}`, {
        status: 'dismissed',
        active: false,
      });
      setEvents((curr) => curr ? curr.filter((e) => e.seriesId !== seriesId) : curr);
      clearSwrCacheByPrefix('events:');
      setSelected((s) => (s && s.seriesId === seriesId ? null : s));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not dismiss series: ${msg}`);
    }
  }

  // Admin: queue the LLM + Google Places venue identifier for an event that
  // has no `venueId`. Mirrors `runIdentifyVenueOne` on AdminEventsPage —
  // surfaced here so admins can fix unidentified venues from where they
  // actually browse (the public events list / cards) instead of having to
  // navigate to the admin events page.
  //
  // The endpoint just enqueues; the queue handler runs the identifier
  // async. We poll the published list for ~30s and merge the result into
  // local state so the row updates in place once the identifier finishes.
  // No prompts, no alerts — admins click and see the result land.
  // Multiple identify calls can run concurrently; we track the in-flight
  // event rowKeys in a Set so each row's spinner is independent.
  const [identifyingIds, setIdentifyingIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  async function identifyVenue(event: ExtractedEvent): Promise<void> {
    if (identifyingIds.has(event.rowKey)) return;
    const force = !!event.venueIdentifyAttemptedAt;
    const previousAttemptIso = event.venueIdentifyAttemptedAt ?? '';
    setIdentifyingIds((prev) => {
      const next = new Set(prev);
      next.add(event.rowKey);
      return next;
    });
    const release = () => setIdentifyingIds((prev) => {
      if (!prev.has(event.rowKey)) return prev;
      const next = new Set(prev);
      next.delete(event.rowKey);
      return next;
    });
    try {
      await api.post(
        `/api/mgmt/events/${encodeURIComponent(event.partitionKey)}/${encodeURIComponent(event.rowKey)}/identify-venue`,
        { force },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not queue the identifier: ${msg}`);
      release();
      return;
    }
    // Poll for the result. We're looking for `venueIdentifyAttemptedAt` to
    // change (queue handler stamps it on every outcome) — when it does we
    // know the identifier finished, so we merge the row into local state
    // and stop. Cap at ~30s; if nothing appears the admin can just retry.
    const window = presetWindow(filters);
    if (!window) {
      toast.error(DATE_WINDOW_ERROR);
      release();
      return;
    }
    const { from, to } = window;
    const fromIso = from.toISOString();
    const toIso = to.toISOString();
    const includeHidden = filters.venueVisibility === 'all';
    const qs = `from=${fromIso}&to=${toIso}${includeHidden ? '&includeHidden=1' : ''}`;
    const cacheKey = `events:${fromIso}|${toIso}|h=${includeHidden ? 1 : 0}`;
    const deadline = Date.now() + 30_000;
    let updated: ExtractedEvent | undefined;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3_000));
      try {
        const fresh = await api.get<ExtractedEvent[]>(
          `/api/events/published?${qs}`,
        );
        const match = fresh.find((e) => e.rowKey === event.rowKey);
        if (match && (match.venueIdentifyAttemptedAt ?? '') !== previousAttemptIso) {
          updated = match;
          // Refresh both the SWR cache and local state so the next render
          // shows the new venue link / audit fields. Use a functional
          // setState so a concurrent identify finishing for a different
          // row doesn't clobber our update.
          writeSwrCache(cacheKey, fresh);
          setEvents((curr) => {
            if (!curr) return fresh;
            return curr.map((e) => e.rowKey === match.rowKey ? { ...e, ...match } : e);
          });
          break;
        }
      } catch { /* transient — keep polling */ }
    }
    release();
    // If we timed out without seeing an update, refresh once anyway so any
    // partial state lands and stop quietly. The next manual refresh or the
    // ambient SWR revalidation will pick it up.
    if (!updated) {
      clearSwrCacheByPrefix('events:');
    }
  }

  // Admin: re-run the AI extractor on this event's original raw post. Use
  // when an event has a wrong date (or any other field) so admins can nudge
  // it through the latest extraction pipeline without leaving the list.
  // Mirrors the identifyVenue flow: spinner via `recalculatingIds`, POST to
  // the per-event endpoint, then poll the published list briefly to reflect
  // the new extraction in place. Re-extraction can change rowKeys (a single
  // raw post may produce different events) so we key the poll on rawId and
  // refetch the whole window when the original row disappears.
  const [recalculatingIds, setRecalculatingIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  async function recalculateDate(event: ExtractedEvent): Promise<void> {
    if (recalculatingIds.has(event.rowKey)) return;
    const rawId = event.rawId;
    const previousUpdatedAt = event.updatedAt ?? '';
    setRecalculatingIds((prev) => {
      const next = new Set(prev);
      next.add(event.rowKey);
      return next;
    });
    const release = () => setRecalculatingIds((prev) => {
      if (!prev.has(event.rowKey)) return prev;
      const next = new Set(prev);
      next.delete(event.rowKey);
      return next;
    });
    try {
      await api.post(`/api/mgmt/events/${encodeURIComponent(event.rowKey)}/recalculate-date`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not queue recalculation: ${msg}`);
      release();
      return;
    }
    // Poll for ~45s. The queue worker deletes prior extracted rows then
    // produces fresh ones, so we wait until either (a) this event's
    // updatedAt advances, or (b) the original row vanishes (extraction
    // produced a new rowKey or judged it noise). Either case = done.
    const window = presetWindow(filters);
    if (!window) {
      toast.error(DATE_WINDOW_ERROR);
      release();
      return;
    }
    const { from, to } = window;
    const fromIso = from.toISOString();
    const toIso = to.toISOString();
    const includeHidden = filters.venueVisibility === 'all';
    // `fresh=1` bypasses the server's 60s response cache so the poll actually
    // observes the queue worker's re-extraction instead of the pre-recalc
    // snapshot (which the cache would otherwise keep serving for up to 60s).
    const qs = `from=${fromIso}&to=${toIso}${includeHidden ? '&includeHidden=1' : ''}&fresh=1`;
    const cacheKey = `events:${fromIso}|${toIso}|h=${includeHidden ? 1 : 0}`;
    const deadline = Date.now() + 45_000;
    let landed = false;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3_000));
      try {
        const fresh = await api.get<ExtractedEvent[]>(`/api/events/published?${qs}`);
        const sameRow = fresh.find((e) => e.rowKey === event.rowKey);
        const advanced = sameRow && (sameRow.updatedAt ?? '') !== previousUpdatedAt;
        const replaced = !sameRow && fresh.some((e) => e.rawId === rawId);
        if (advanced || replaced || !sameRow) {
          writeSwrCache(cacheKey, fresh);
          setEvents(fresh);
          landed = true;
          break;
        }
      } catch { /* transient — keep polling */ }
    }
    release();
    if (!landed) {
      // Time-out fallback: drop the cache so the next visit refetches fresh.
      clearSwrCacheByPrefix('events:');
    }
  }

  // Admin: flag an event's date/time as wrong. Without a correction the event
  // is badged "Wrong date" (stays visible) and logged for AI-training review;
  // with a correction the date is fixed in place. The endpoint responds
  // synchronously, so we refresh the current window once.
  const [markingWrongIds, setMarkingWrongIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  async function markWrongDate(event: ExtractedEvent, correction?: DateCorrection): Promise<void> {
    if (markingWrongIds.has(event.rowKey)) return;
    setMarkingWrongIds((prev) => {
      const next = new Set(prev);
      next.add(event.rowKey);
      return next;
    });
    const release = () => setMarkingWrongIds((prev) => {
      if (!prev.has(event.rowKey)) return prev;
      const next = new Set(prev);
      next.delete(event.rowKey);
      return next;
    });
    try {
      const updated = await api.post<ExtractedEvent>(
        `/api/mgmt/events/${encodeURIComponent(event.rowKey)}/flag-wrong-date`,
        correction ?? {},
      );
      // Optimistically fold the authoritative date/flag fields onto the row so
      // the correction (or "wrong date" badge) shows instantly, independent of
      // the public endpoint's response cache. Only the date/flag columns are
      // copied so venue/series/tag enrichment already on the row is preserved.
      setEvents((prev) => prev
        ? prev.map((ev) => ev.rowKey === event.rowKey
          ? {
              ...ev,
              startUtc: updated.startUtc,
              endUtc: updated.endUtc,
              isAllDay: updated.isAllDay,
              dateFlaggedWrong: updated.dateFlaggedWrong,
              dateFlaggedWrongAt: updated.dateFlaggedWrongAt,
              dateFlaggedWrongBy: updated.dateFlaggedWrongBy,
              partitionKey: updated.partitionKey,
              updatedAt: updated.updatedAt,
            }
          : ev)
        : prev);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not update event: ${msg}`);
      release();
      return;
    }
    toast.success(correction ? 'Date corrected.' : 'Marked as wrong date/time.');
    const window = presetWindow(filters);
    if (!window) {
      toast.error(DATE_WINDOW_ERROR);
      release();
      return;
    }
    const { from, to } = window;
    const fromIso = from.toISOString();
    const toIso = to.toISOString();
    const includeHidden = filters.venueVisibility === 'all';
    // `fresh=1` bypasses the server's 60s response cache so this reconcile
    // refetch reflects the edit instead of repainting the stale pre-edit
    // snapshot over our optimistic update.
    const qs = `from=${fromIso}&to=${toIso}${includeHidden ? '&includeHidden=1' : ''}&fresh=1`;
    const cacheKey = `events:${fromIso}|${toIso}|h=${includeHidden ? 1 : 0}`;
    try {
      const fresh = await api.get<ExtractedEvent[]>(`/api/events/published?${qs}`);
      writeSwrCache(cacheKey, fresh);
      setEvents(fresh);
    } catch {
      clearSwrCacheByPrefix('events:');
    }
    release();
  }

  const visible = useMemo(() => {
    if (!events) return [];
    const base = applyFilters(events, filters);
    let filtered = favoritesOnly
      ? base.filter((e) => isEventHighlighted(e, favorites))
      : base;
    // "My taste only" — score every candidate against the user's profile,
    // hide events the user has already marked as taste (they're already known)
    // or marked as "not interested", drop everything below the threshold,
    // and sort by score desc.
    if (tasteOnly && favorites.tasteIds.size >= MIN_TASTE_FOR_SCORING) {
      const markedEvents = events.filter(e => favorites.tasteIds.has(e.rowKey));
      const profile = buildTasteProfile(markedEvents);
      const scored = filtered
        .filter(e => !profile.markedRowKeys.has(e.rowKey))
        .filter(e => !favorites.dislikeIds.has(e.rowKey))
        .map(e => ({ e, score: scoreEvent(e, profile, tasteEmbeddingScores.get(e.rowKey), tasteSettings.weights).score }))
        .filter(x => x.score >= MIN_TASTE_SCORE_THRESHOLD)
        .sort((a, b) => b.score - a.score);
      filtered = scored.map(x => x.e);
    }
    return filtered;
  }, [events, filters, favoritesOnly, tasteOnly, favorites, tasteEmbeddingScores, tasteSettings.weights]);

  // Resolve effective grouping. When the user hasn't picked any group-by
  // levels and the sort is by date, fall back to the legacy date grouping so
  // existing visitors don't lose the day-by-day breakdown.
  const effectiveGroupBy: GroupByKey[] = useMemo(() => {
    if (groupBy.length > 0) return groupBy;
    if (filters.sort === 'date-asc' || filters.sort === 'date-desc') return ['date'];
    return [];
  }, [groupBy, filters.sort]);

  const groupedTree = useMemo<EventGroupNode[] | null>(() => {
    if (effectiveGroupBy.length === 0) return null;
    return groupEvents(visible, effectiveGroupBy);
  }, [visible, effectiveGroupBy]);

  return (
    <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
      <h1 className="mb-2">Upcoming events</h1>
      <p className="lead">
        Bars, restaurants, music, art, and community happenings in Murray Hill, Riverside, and Avondale.
      </p>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <EventFiltersBar events={events} filters={filters} setFilters={setFilters} />

      {viewMode === 'list' && <OnViewShelf />}

      <div className="mb-3">
        <GroupBySelector value={groupBy} onChange={setGroupBy} />
      </div>

      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        {events !== null ? (
          <p className="text-muted small mb-0" aria-live="polite">
            Showing {visible.length} of {events.length} event{events.length === 1 ? '' : 's'}
          </p>
        ) : (
          <span />
        )}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isAuthed && (
            <button
              type="button"
              className={`btn btn-sm ${favoritesOnly ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => {
                const next = !favoritesOnly;
                setFavoritesOnly(next);
                writeFiltersToUrl(
                  filters,
                  { ...extraParams(), fav: next ? '1' : undefined },
                  (p) => setParams(p, { replace: true })
                );
              }}
              aria-pressed={favoritesOnly}
              title="Show only events you've saved, plus events at venues and series you follow"
            >
              <span aria-hidden="true">★</span> Favorites only
            </button>
          )}
          {isAuthed && (
            <button
              type="button"
              className={`btn btn-sm ${tasteOnly ? 'btn-info' : 'btn-outline-info'}`}
              onClick={() => {
                const next = !tasteOnly;
                setTasteOnly(next);
                writeFiltersToUrl(
                  filters,
                  { ...extraParams(), taste: next ? '1' : undefined },
                  (p) => setParams(p, { replace: true })
                );
              }}
              aria-pressed={tasteOnly}
              disabled={favorites.tasteIds.size < MIN_TASTE_FOR_SCORING}
              title={
                favorites.tasteIds.size < MIN_TASTE_FOR_SCORING
                  ? `Save at least ${MIN_TASTE_FOR_SCORING} events (👍) to unlock personalized recommendations`
                  : 'Show events recommended for you, based on the events you’ve saved'
              }
            >
              <i className="bi bi-stars me-1" aria-hidden="true" /> Recommended
            </button>
          )}
          {viewMode === 'list' && (
          <div className="btn-group" role="group" aria-label="List density">
            <button
              type="button"
              className={`btn btn-sm ${density === 'compact' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setDensity('compact')}
              aria-pressed={density === 'compact'}
              title="Compact list"
            >
              <i className="bi bi-list-task me-1" aria-hidden="true" />
              Compact
            </button>
            <button
              type="button"
              className={`btn btn-sm ${density === 'cards' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setDensity('cards')}
              aria-pressed={density === 'cards'}
              title="Card grid"
            >
              <i className="bi bi-grid me-1" aria-hidden="true" />
              Cards
            </button>
          </div>
        )}
        </div>
      </div>

      {events === null && !error && <LoadingBlock label="Loading events…" />}

      {events && visible.length === 0 && viewMode === 'list' && (
        <div className="alert alert-info">No events match the current filters.</div>
      )}

      {viewMode === 'calendar' && events !== null && (
        <CalendarView events={visible} onSelectEvent={setSelected} />
      )}

      {viewMode === 'list' && events && visible.length > 0 && (
        <>
          {groupedTree
            ? renderGroupTree(groupedTree, 0)
            : density === 'compact'
              ? renderCompactList(visible, !effectiveGroupBy.includes('date'))
              : renderCardGrid(visible)}
        </>
      )}

      <EventDetailModal
        event={selected}
        onClose={() => setSelected(null)}
        onEdit={isAdmin ? (e) => { setSelected(null); setEditingEvent(e); } : undefined}
        onReject={isAdmin ? (e) => promptHideEvent(e) : undefined}
        onHideVenue={isAdmin ? (e) => { if (e.venueId) void hideVenue(e.venueId, e.venueName ?? e.locationText ?? ''); } : undefined}
        onHideSeries={isAdmin ? (e) => { if (e.seriesId) void hideSeries(e.seriesId, e.seriesName ?? ''); } : undefined}
        onShiftSeries={isAdmin ? async (e, weekday) => {
          // Shift every row sharing this event's recurringSeriesId onto the
          // requested weekday, then refresh both the modal-selected event
          // and the underlying list. Errors bubble up to the picker for
          // inline display.
          const result = await api.post<{ shifted?: number; siblingsFound?: number }>(
            `/api/mgmt/events/${encodeURIComponent(e.rowKey)}/shift-series`,
            { weekday }
          );
          // Refresh the events list so shifted dates appear immediately.
          const window = presetWindow(filters);
          if (!window) throw new Error(DATE_WINDOW_ERROR);
          const { from, to } = window;
          const fromIso = from.toISOString();
          const toIso = to.toISOString();
          const includeHidden = filters.venueVisibility === 'all';
          const qs = `from=${fromIso}&to=${toIso}${includeHidden ? '&includeHidden=1' : ''}`;
          const cacheKey = `events:${fromIso}|${toIso}|h=${includeHidden ? 1 : 0}`;
          try {
            const fresh = await api.get<ExtractedEvent[]>(`/api/events/published?${qs}`);
            writeSwrCache(cacheKey, fresh);
            setEvents(fresh);
          } catch {
            clearSwrCacheByPrefix('events:');
          }
          setSelected(null);
          toast.success(`Shifted ${result?.shifted ?? 0} of ${result?.siblingsFound ?? 0} occurrences in this series.`);
        } : undefined}
        onEventUpdated={isAdmin ? (updated) => {
          setEvents((curr) => curr ? curr.map((e) => e.rowKey === updated.rowKey ? { ...e, ...updated } : e) : curr);
          setSelected((curr) => curr && curr.rowKey === updated.rowKey ? { ...curr, ...updated } : curr);
        } : undefined}
      />
      <EventEditModal
        event={editingEvent}
        venues={adminVenues}
        seriesList={adminSeries}
        onClose={() => setEditingEvent(null)}
        onSaved={(saved) => {
          setEvents((curr) => curr ? curr.map((e) => e.rowKey === saved.rowKey ? { ...e, ...saved } : e) : curr);
          // Public events SWR cache may now be stale; clear every cached window.
          clearSwrCacheByPrefix('events:');
        }}
      />
      {rejectModal}
    </SectionTabs>
  );

  function renderGroupTree(nodes: EventGroupNode[], depth: number) {
    return nodes.map((node) => {
      const headingId = `grp-${depth}-${node.key}-${cssSafe(node.value)}`;
      const headerInner = (
        <>
          <span className="mh-event-group__title">
            <span className="text-muted small me-2">{groupByLabel(node.key)}:</span>
            {node.label}
          </span>
          <span className="mh-event-group__count">
            {node.count} event{node.count === 1 ? '' : 's'}
          </span>
        </>
      );
      const headerProps = { id: headingId, className: 'mh-event-group__header' };
      const header =
        depth === 0 ? <h2 {...headerProps}>{headerInner}</h2>
        : depth === 1 ? <h3 {...headerProps}>{headerInner}</h3>
        : depth === 2 ? <h4 {...headerProps}>{headerInner}</h4>
        : <h5 {...headerProps}>{headerInner}</h5>;
      return (
        <section
          key={`${depth}-${node.key}-${node.value}`}
          className={`mh-event-group mh-event-group--depth-${depth}`}
          aria-labelledby={headingId}
        >
          {header}
          {node.children.length > 0
            ? renderGroupTree(node.children, depth + 1)
            : density === 'compact'
              ? renderCompactList(node.events, !effectiveGroupBy.includes('date'))
              : renderCardGrid(node.events)}
        </section>
      );
    });
  }

  function renderCompactList(items: ExtractedEvent[], showDate: boolean = false) {
    return (
      <ul className="list-unstyled mh-event-list" aria-label="Event results">
        {items.map((e) => {
          const imageSrc = resolveImage(e.imageUrl);
          const allDay = isAllDayEvent(e);
          const time = timePillParts(e);
          const tile = dateTileParts(e);
          const savedClass = favorites.isSaved(e.rowKey) ? ' mh-event-row--saved' : '';
          return (
            <li key={e.rowKey} className={`mh-event-row${savedClass}`}>
              <DateProofSchedule event={e}>
              <div
                className={`mh-event-row__time flex-shrink-0${allDay ? ' mh-event-row__time--allday' : ''}`}
              >
                {showDate && (
                  <span className="mh-event-row__time-date">{shortDateLabel(e)}</span>
                )}
                <span className="mh-event-row__time-start">{time.start}</span>
                {time.end && (
                  <span className="mh-event-row__time-end">{time.end}</span>
                )}
              </div>
              </DateProofSchedule>
              <div className="mh-event-row__body">
                <div className="mh-event-row__title-line">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-start mh-event-title mh-event-row__title"
                    onClick={() => setSelected(e)}
                  >
                    {e.title}
                  </button>
                  <MatchBadge match={matchFor(e)} className="mh-event-row__chip" />
                  <EventReviewWarningChips warnings={nonTemporalWarnings(e.reviewWarnings)} />
                  {e.dateFlaggedWrong && (
                    <span
                      className="badge mh-wrong-date-chip mh-event-row__chip"
                      title="An admin flagged this date/time as wrong"
                    >
                      <i className="bi bi-calendar-x" aria-hidden="true" /> Wrong date
                    </span>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted mh-event-row__edit"
                      onClick={(ev) => { ev.stopPropagation(); setEditingEvent(e); }}
                      aria-label={`Edit ${e.title}`}
                      title="Edit event"
                    >
                      <i className="bi bi-pencil-square" aria-hidden="true" />
                    </button>
                  )}
                  {isAdmin && (
                    <EventDateMenu
                      event={e}
                      variant="compact"
                      recalcBusy={recalculatingIds.has(e.rowKey)}
                      markBusy={markingWrongIds.has(e.rowKey)}
                      onRecalculate={(ev) => void recalculateDate(ev)}
                      onMarkWrong={(ev, correction) => void markWrongDate(ev, correction)}
                    />
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-danger mh-event-row__edit"
                      onClick={(ev) => { ev.stopPropagation(); promptHideEvent(e); }}
                      aria-label={`Hide event ${e.title}`}
                      title={`Hide event "${e.title}" (mark rejected — removes from public list)`}
                    >
                      <i className="bi bi-eye-slash" aria-hidden="true" />
                    </button>
                  )}
                  {e.eventType && (
                    <span className="badge text-bg-primary mh-event-row__chip">
                      {formatTag(e.eventType)}
                    </span>
                  )}
                  {e.priceInfo && (
                    <span className="badge text-bg-success mh-event-row__chip">{e.priceInfo}</span>
                  )}
                  {e.ageRestriction && (
                    <span className="badge text-bg-warning text-dark mh-event-row__chip">
                      {formatTag(e.ageRestriction)}
                    </span>
                  )}
                  {e.recurringPattern && e.recurringPattern !== 'one-time' && (
                    <span
                      className="badge text-bg-info text-dark mh-event-row__chip"
                      title={
                        e.recurringIsPermanent
                          ? `Ongoing ${formatTag(e.recurringPattern)} series${e.recurringDaysOfWeek?.length ? ` on ${e.recurringDaysOfWeek.join(', ')}` : ''}`
                          : e.recurringDaysOfWeek?.length
                            ? `Recurs ${formatTag(e.recurringPattern)} on ${e.recurringDaysOfWeek.join(', ')}`
                            : `Recurs ${formatTag(e.recurringPattern)}`
                      }
                    >
                      {e.recurringIsPermanent ? '♾️' : '🔁'} {e.recurringIsPermanent ? `Ongoing ${formatTag(e.recurringPattern)}` : formatTag(e.recurringPattern)}
                    </span>
                  )}
                  <StaleRecurringChip event={e} className="mh-event-row__chip" />
                </div>
                <div className="mh-event-row__meta">
                  <time dateTime={e.startUtc} className="visually-hidden">
                    {formatRange(e, dateProofRangeOptions(e))}
                  </time>
                  {(e.venueName || e.locationText) && (
                    <span className="mh-event-row__venue">
                      <i className="bi bi-geo-alt me-1" aria-hidden="true" />
                      {e.venueId ? (
                        <Link to={`/venues/${e.venueId}`}>{e.venueName ?? e.locationText}</Link>
                      ) : (
                        e.venueName ?? e.locationText
                      )}
                      {e.neighborhood && <span className="text-muted"> · {e.neighborhood}</span>}
                      {e.venueId && (
                        <EntityFollowChip
                          kind="venue"
                          targetId={e.venueId}
                          targetName={e.venueName ?? e.locationText ?? 'this venue'}
                          className="ms-2"
                        />
                      )}
                      {isAdmin && e.venueId && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-2 text-danger align-baseline"
                          onClick={(ev) => { ev.stopPropagation(); void hideVenue(e.venueId!, e.venueName ?? e.locationText ?? ''); }}
                          aria-label={`Decline venue ${e.venueName ?? e.locationText}`}
                          title={`Decline venue "${e.venueName ?? e.locationText}" — hides all of its events`}
                        >
                          <i className="bi bi-eye-slash" aria-hidden="true" />
                        </button>
                      )}
                      {isAdmin && !e.venueId && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-2 align-baseline mh-identify-venue-btn"
                          onClick={(ev) => { ev.stopPropagation(); void identifyVenue(e); }}
                          disabled={identifyingIds.has(e.rowKey)}
                          aria-label={`Identify venue for ${e.title}`}
                          title={
                            identifyingIds.has(e.rowKey)
                              ? 'Identifying…'
                              : e.venueIdentifyAttemptedAt
                                ? `Re-run identifier (last result: ${e.venueIdentifyResult ?? 'unknown'})`
                                : 'Identify venue (LLM + Google Places)'
                          }
                        >
                          <i
                            className={`bi ${identifyingIds.has(e.rowKey) ? 'bi-arrow-repeat mh-spin' : 'bi-search'}`}
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </span>
                  )}
                  {e.seriesId && e.seriesName && (
                    <span className="badge rounded-pill text-bg-info text-dark mh-event-row__chip">
                      <i className="bi bi-collection me-1" aria-hidden="true" />
                      <Link to={`/series/${e.seriesId}`} className="text-reset text-decoration-none">{e.seriesName}</Link>
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1 text-danger align-baseline"
                          onClick={(ev) => { ev.stopPropagation(); void hideSeries(e.seriesId!, e.seriesName ?? ''); }}
                          aria-label={`Dismiss series ${e.seriesName}`}
                          title={`Dismiss series "${e.seriesName}" (hides all of its events)`}
                        >
                          <i className="bi bi-eye-slash" aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  )}
                  {e.seriesId && e.seriesName && (
                    <EntityFollowChip
                      kind="series"
                      targetId={e.seriesId}
                      targetName={e.seriesName}
                      className="ms-1"
                    />
                  )}
                  <EventTagChips
                    event={e}
                    max={3}
                    chipClassName="badge rounded-pill text-bg-light border mh-event-row__chip"
                    className="d-inline-flex flex-wrap align-items-center gap-1 position-relative"
                    onChange={isAdmin ? (updated) => setEvents((curr) => curr ? curr.map((x) => x.rowKey === updated.rowKey ? { ...x, ...updated } : x) : curr) : undefined}
                  />
                </div>
                <div className="mh-event-row__actions">
                  <EventReactionButtons event={e} />
                </div>
              </div>
              <button
                type="button"
                className="mh-event-row__thumb-btn"
                onClick={() => setSelected(e)}
                aria-label={`View details for ${e.title}`}
              >
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt=""
                    loading="lazy"
                    className="mh-event-row__thumb"
                  />
                ) : (
                  <div className="mh-event-row__thumb mh-event-row__thumb--date" aria-hidden="true">
                    <span className={`mh-event-row__thumb-day${tile.weekday ? '' : ' mh-event-row__thumb-day--month'}`}>
                      {tile.day}
                    </span>
                    <span className="mh-event-row__thumb-month">
                      {tile.month}
                    </span>
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  function renderCardGrid(items: ExtractedEvent[]) {
    return (
      <ul className="list-unstyled row g-3" aria-label="Event results">
        {items.map((e) => {
          const imageSrc = resolveImage(e.imageUrl);
          const tile = dateTileParts(e);
          const savedClass = favorites.isSaved(e.rowKey) ? ' mh-event-card--saved' : '';
          return (
            <li key={e.rowKey} className="col-sm-6 col-lg-4 col-xl-3">
              <article className={`card h-100 mh-event-card${savedClass}`}>
                <button
                  type="button"
                  className="mh-event-image-btn"
                  onClick={() => setSelected(e)}
                  aria-label={`View details for ${e.title}`}
                >
                  {imageSrc ? (
                    <img src={imageSrc} alt="" loading="lazy" className="card-img-top mh-event-image" />
                  ) : (
                    <div className="mh-event-date-tile" aria-hidden="true">
                      {tile.weekday ? (
                        <span className="mh-event-date-tile__weekday">
                          {tile.weekday}
                        </span>
                      ) : null}
                      <span className={`mh-event-date-tile__day${tile.weekday ? '' : ' mh-event-date-tile__day--month'}`}>
                        {tile.day}
                      </span>
                      <span className="mh-event-date-tile__month">
                        {tile.month}
                      </span>
                    </div>
                  )}
                </button>
                <div className="card-body d-flex flex-column">
                  <h2 className="h6 mb-1">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start mh-event-title"
                      onClick={() => setSelected(e)}
                    >
                      {e.title}
                    </button>
                    {e.dateFlaggedWrong && (
                      <span
                        className="badge mh-wrong-date-chip ms-2 align-baseline"
                        title="An admin flagged this date/time as wrong"
                      >
                        <i className="bi bi-calendar-x" aria-hidden="true" /> Wrong date
                      </span>
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2 text-muted small align-baseline"
                        onClick={(ev) => { ev.stopPropagation(); setEditingEvent(e); }}
                        aria-label={`Edit ${e.title}`}
                        title="Edit event"
                      >
                        <i className="bi bi-pencil-square" aria-hidden="true" />
                      </button>
                    )}
                    {isAdmin && (
                      <EventDateMenu
                        event={e}
                        variant="card"
                        recalcBusy={recalculatingIds.has(e.rowKey)}
                        markBusy={markingWrongIds.has(e.rowKey)}
                        onRecalculate={(ev) => void recalculateDate(ev)}
                        onMarkWrong={(ev, correction) => void markWrongDate(ev, correction)}
                      />
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-1 text-danger small align-baseline"
                        onClick={(ev) => { ev.stopPropagation(); promptHideEvent(e); }}
                        aria-label={`Hide event ${e.title}`}
                        title={`Hide event "${e.title}" (mark rejected — removes from public list)`}
                      >
                        <i className="bi bi-eye-slash" aria-hidden="true" />
                      </button>
                    )}
                  </h2>
                  <div className="small text-muted mb-2 d-flex flex-column align-items-start gap-1">
                    <time dateTime={e.startUtc}>{formatRange(e, dateProofRangeOptions(e))}</time>
                    <DateProofWarning event={e} />
                  </div>
                  {(e.venueName || e.locationText) && (
                    <p className="small mb-2">
                      {e.venueId ? (
                        <Link to={`/venues/${e.venueId}`}>{e.venueName ?? e.locationText}</Link>
                      ) : (
                        <span>{e.venueName ?? e.locationText}</span>
                      )}
                      {e.neighborhood && <span className="text-muted"> · {e.neighborhood}</span>}
                      {e.venueId && (
                        <EntityFollowChip
                          kind="venue"
                          targetId={e.venueId}
                          targetName={e.venueName ?? e.locationText ?? 'this venue'}
                          offLabel
                          className="ms-2"
                        />
                      )}
                      {isAdmin && e.venueId && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-2 text-danger align-baseline"
                          onClick={(ev) => { ev.stopPropagation(); void hideVenue(e.venueId!, e.venueName ?? e.locationText ?? ''); }}
                          aria-label={`Decline venue ${e.venueName ?? e.locationText}`}
                          title={`Decline venue "${e.venueName ?? e.locationText}" — hides all of its events`}
                        >
                          <i className="bi bi-eye-slash" aria-hidden="true" />
                        </button>
                      )}
                      {isAdmin && !e.venueId && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-2 align-baseline mh-identify-venue-btn"
                          onClick={(ev) => { ev.stopPropagation(); void identifyVenue(e); }}
                          disabled={identifyingIds.has(e.rowKey)}
                          aria-label={`Identify venue for ${e.title}`}
                          title={
                            identifyingIds.has(e.rowKey)
                              ? 'Identifying…'
                              : e.venueIdentifyAttemptedAt
                                ? `Re-run identifier (last result: ${e.venueIdentifyResult ?? 'unknown'})`
                                : 'Identify venue (LLM + Google Places)'
                          }
                        >
                          <i
                            className={`bi ${identifyingIds.has(e.rowKey) ? 'bi-arrow-repeat mh-spin' : 'bi-search'}`}
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </p>
                  )}
                  {e.seriesId && e.seriesName && (
                    <p className="small mb-2">
                      <Link to={`/series/${e.seriesId}`} className="badge rounded-pill text-bg-info text-dark text-decoration-none">
                        <i className="bi bi-collection me-1" aria-hidden="true" />
                        {e.seriesName}
                      </Link>
                      {e.seriesId && (
                        <EntityFollowChip
                          kind="series"
                          targetId={e.seriesId}
                          targetName={e.seriesName}
                          offLabel
                          className="ms-2"
                        />
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1 text-danger align-baseline"
                          onClick={(ev) => { ev.stopPropagation(); void hideSeries(e.seriesId!, e.seriesName ?? ''); }}
                          aria-label={`Dismiss series ${e.seriesName}`}
                          title={`Dismiss series "${e.seriesName}" (hides all of its events)`}
                        >
                          <i className="bi bi-eye-slash" aria-hidden="true" />
                        </button>
                      )}
                    </p>
                  )}
                  <div className="mb-2 d-flex flex-wrap gap-1">
                    <MatchBadge match={matchFor(e)} />
                    <EventReviewWarningChips warnings={nonTemporalWarnings(e.reviewWarnings)} />
                    {e.eventType && <span className="badge text-bg-primary small">{formatTag(e.eventType)}</span>}
                    {e.priceInfo && <span className="badge text-bg-success small">{e.priceInfo}</span>}
                    {e.ageRestriction && <span className="badge text-bg-warning text-dark small">{formatTag(e.ageRestriction)}</span>}
                    {e.recurringPattern && e.recurringPattern !== 'one-time' && (
                      <span
                        className="badge text-bg-info text-dark small"
                        title={
                          e.recurringDaysOfWeek?.length
                            ? `Recurs ${formatTag(e.recurringPattern)} on ${e.recurringDaysOfWeek.join(', ')}`
                            : `Recurs ${formatTag(e.recurringPattern)}`
                        }
                      >
                        🔁 {formatTag(e.recurringPattern)}
                      </span>
                    )}
                    <StaleRecurringChip event={e} size=" small" />
                    <EventTagChips
                      event={e}
                      max={3}
                      chipClassName="badge rounded-pill text-bg-light border small"
                      className="d-inline-flex flex-wrap align-items-center gap-1 position-relative"
                      onChange={isAdmin ? (updated) => setEvents((curr) => curr ? curr.map((x) => x.rowKey === updated.rowKey ? { ...x, ...updated } : x) : curr) : undefined}
                    />
                  </div>
                  {e.description && <p className="small mb-2 mh-event-desc">{e.description}</p>}
                  <div className="mt-auto">
                    <div className="mh-event-card__actions">
                      <EventReactionButtons event={e} />
                    </div>
                    {(e.ticketUrl || e.rawUrl) && (
                      <div className="small d-flex flex-wrap gap-2 mt-2">
                        {e.ticketUrl && (
                          <a href={e.ticketUrl} target="_blank" rel="noreferrer noopener">
                            Tickets / details
                          </a>
                        )}
                        {e.rawUrl && (
                          <a href={e.rawUrl} target="_blank" rel="noreferrer noopener">
                            View original post
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    );
  }
}
